import React, { useCallback } from 'react';
import type { InjectedIntlProps } from 'react-intl';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import BookIcon from '@atlaskit/icon/glyph/book';
import HipchatLobbyIcon from '@atlaskit/icon/glyph/hipchat/lobby';
import QuestionsIcon from '@atlaskit/icon/glyph/questions';
import Tooltip from '@atlaskit/tooltip';
import { UI_EVENT_TYPE } from '@atlassian/analytics-web-react';
import { SkeletonItem } from '@atlassian/help-center-common-component/skeleton-item';
import { useExternalLinkIcon } from '../use-external-link-icon';
import messages from './messages';
import { LinkWrapper, IconWrapper, ContentWrapper, iconDimension } from './styled';
import type { ExternalLinkObject } from 'rest/external-links/types';

export type Props = ExternalLinkObject & InjectedIntlProps & { position?: number; isLoggedIn: boolean };
export const ExternalLinkItem: React.FC<Props> = (props) => {
    const {
        title,
        description,
        resourceType,
        link,
        position,
        isLoggedIn,
        intl: { formatMessage },
    } = props;

    const { createAnalyticsEvent } = useAnalyticsEvents();
    const { data: iconUrl, isLoading: isIconLoading } = useExternalLinkIcon({ link, isLoggedIn });

    const getIconLabel = (iconType: ExternalLinkObject['resourceType']) => {
        switch (iconType) {
            case 'request-form': {
                return formatMessage(messages.externalLinkRequestFormIconLabel);
            }
            case 'knowledge': {
                return formatMessage(messages.externalLinkKnowledgeIconLabel);
            }
            case 'channel':
            default: {
                return formatMessage(messages.externalLinkHelpChannelIconLabel);
            }
        }
    };

    const getFallbackIcon = (iconType: ExternalLinkObject['resourceType']) => {
        switch (iconType) {
            case 'request-form': {
                return <HipchatLobbyIcon label={getIconLabel('request-form')} />;
            }
            case 'knowledge': {
                return <BookIcon label={getIconLabel('knowledge')} />;
            }
            case 'channel':
            default: {
                return <QuestionsIcon label={getIconLabel('channel')} />;
            }
        }
    };

    const onLinkClick = useCallback(() => {
        const analyticsEvent = createAnalyticsEvent({
            resourceType,
            analyticsType: UI_EVENT_TYPE,
            action: 'clicked',
            link_position: position,
        });

        analyticsEvent.fire();
    }, [createAnalyticsEvent, position, resourceType]);

    const getIcon = () => {
        if (isIconLoading) {
            return <SkeletonItem height={iconDimension} width={iconDimension} />;
        }

        if (iconUrl) {
            return <img src={iconUrl} alt={getIconLabel(resourceType)} />;
        }

        return getFallbackIcon(resourceType);
    };

    return (
        <LinkWrapper className="cv-exclude-from-customisation">
            <Tooltip content={formatMessage(messages.externalLinkHoverTooltip)} position="mouse">
                <a target="_blank" rel="noopener noreferrer" href={link} onClick={onLinkClick}>
                    <IconWrapper>{getIcon()}</IconWrapper>
                    <ContentWrapper>
                        <h5>{title}</h5>
                        <p>{description}</p>
                    </ContentWrapper>
                </a>
            </Tooltip>
        </LinkWrapper>
    );
};
